import React, { forwardRef, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import Summary from "./Summary";
import Prescription from "../../Prescription/Prescription";
import Reference from "./Reference";
import Certificate from "../../../components/Certificate";
import { FaFilePdf } from 'react-icons/fa';
import { MediaKind } from './types';

interface ChatMessageProps {
  message: string;
  timestamp: string;
  isDoctor: boolean;
  imageUrl?: string;
  fileName?: string;
  fileSize?: number;
  mediaKind?: MediaKind;
  doctorName: string;
  doctorSpecialty: string;
  avatar?: string;
  onReply?: (msg: any) => void;
  messageID?: string;
  isHighlighted?: boolean;
  onReplyQuoteClick?: (messageID: string) => void;
  replyTo?: any;
  userDokter?: string;
  patientName?: string;
  onMediaClick?: (url: string, fileName?: string, type?: "image" | "file" | "video" | "audio") => void;
}

const highlightFade = keyframes`
  0% {
    background-color: rgba(172, 172, 172, 0.6);
    border-radius: 10px;
  }
  70% {
    background-color: rgba(218, 218, 215, 0.6);
    border-radius: 10px;
  }
  100% {
    background-color: transparent;
    border-radius: 10px;
  }
`;

const MessageContainer = styled.div<{ isDoctor?: boolean; translateX?: number; isHighlighted?: boolean }>`
  display: flex;
  align-items: flex-start;
  flex-direction: ${({ isDoctor }) => (isDoctor ? 'row' : 'row-reverse')};
  margin: 4px 2px;
  position: relative;
  transition: transform 0.3s ease;
  transform: translateX(${({ translateX }) => translateX || 0}px);
  ${({ isHighlighted }) =>
  isHighlighted &&
  css`
    animation: ${highlightFade} 2s ease-out;
  `}
`;

const MessageWrapper = styled.div`
  position: relative;
  z-index: 1;
  max-width: 80%;
`;

const Avatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin: 4px;
`;

const MessageBubble = styled.div<{ isDoctor?: boolean }>`
  position: relative;
  background-color: ${({ isDoctor }) => (isDoctor ? '#fff' : '#f1f1f1')};
  color: black;
  padding: 5px 10px;
  border-radius: ${({ isDoctor }) =>
    isDoctor ? '0px 12px 12px 12px' : '12px 0px 12px 12px'};
  max-width: 100%;
  word-break: break-word;
  box-shadow: 0px 1px 3px rgba(0,0,0,0.1);
`;

const Timestamp = styled.span`
  display: block;
  font-size: 11px;
  color: #999;
  margin-top: 4px; // Jarak dari konten pesan
  text-align: right;
`;

const ReplyQuote = styled.div<{ clickable?: boolean }>`
  padding: 6px;
  margin-bottom: 6px;
  border-radius: 2px;
  font-size: 12px;
  color: #666;
  max-width: 250px;
  background-color: #e0e0e0ff;
  border-radius: 10px;
  border-left: 3px solid #ed1c24;
  ${({ clickable }) => clickable && `
    cursor: pointer;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: #c9c9c9ff;
    }
  `}
`;

const QuoteSender = styled.div`
  font-weight: 600;
  color: #333;
`;

const QuoteMessage = styled.div`
  margin-top: 2px;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SystemBubble = styled.div`
  background-color: #f4c1c1ff;
  color: #000000ff;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 13px;
  display: inline-block;
  margin: 10px auto;
`;

const getFileIcon = (fileName?: string): React.ReactNode => {
  if (!fileName) return "📄";
  const ext = fileName.split(".").pop()?.toLowerCase();
  if (ext === "pdf") {
    return <FaFilePdf color="#E53935" size={24} />;
  }
  if (["doc", "docx"].includes(ext || "")) return "📝";
  if (["xls", "xlsx"].includes(ext || "")) return "📊";
  if (["zip", "rar", "7z"].includes(ext || "")) return "🗜️";
  if (["mp4", "mov", "avi"].includes(ext || "")) return "🎬";
  if (["mp3", "wav", "ogg"].includes(ext || "")) return "🎵";
  if (["ppt", "pptx"].includes(ext || "")) return "📑";
  return "📄";
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes || bytes === 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const ChatMessage = forwardRef<HTMLDivElement, ChatMessageProps>(({
  message,
  timestamp,
  isDoctor,
  imageUrl,
  fileName,
  fileSize,
  mediaKind,
  onMediaClick,
  doctorName,
  doctorSpecialty,
  avatar,
  onReply,
  messageID,
  isHighlighted,
  onReplyQuoteClick,
  replyTo: replyToProp,
  userDokter,
  patientName,
}, ref) => {
  const messageRef = useRef<HTMLDivElement>(null);
  const [translateX, setTranslateX] = useState(0);
  const [startX, setStartX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const SWIPE_THRESHOLD = 80;
  const MAX_TRANSLATE = 60;
  
  let parsedMessage: any;

  try {
    parsedMessage = JSON.parse(message);
  } catch {
    parsedMessage = message;
  }

  const isObjectMessage = typeof parsedMessage === "object" && parsedMessage !== null;
  const replyTo = replyToProp || parsedMessage?.replyTo;

  const resolvedImageUrl =
    imageUrl || parsedMessage?.Origin?.Url || parsedMessage?.url;

  const resolvedFileName =
    fileName || parsedMessage?.FileName || parsedMessage?.fileName;
  const resolvedFileSize = fileSize;
  const resolvedMediaKind: MediaKind | undefined =
    mediaKind ||
    parsedMessage?.type ||
    (resolvedFileName?.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i)
      ? 'image'
      : resolvedFileName?.match(/\.(mp3|wav|ogg|m4a|aac)$/i)
      ? 'audio'
      : resolvedFileName?.match(/\.(mp4|mov|avi|mkv|webm)$/i)
      ? 'video'
      : undefined);

  // ✅ SYSTEM MESSAGE DETECTION
  if (typeof message === "string" && (message.includes("accepted|") || message.includes("end|"))) {
    const label = message.includes("accepted|") ? "Konsultasi dimulai" : "Konsultasi berakhir";
    return (
      <div style={{ textAlign: "center" }}>
        <SystemBubble>{`${label} ${timestamp}`}</SystemBubble>
      </div>
    );
  }

  if (parsedMessage?.message === 'end') {
    return null;
  }

  if (parsedMessage?.message === 'start') {
    return null;
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    setStartX(e.touches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;

    const currentX = e.touches[0].clientX;
    const diff = currentX - startX;

    if (diff > 0) {
      const translate = Math.min(diff, MAX_TRANSLATE);
      setTranslateX(translate);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const currentX = e.changedTouches[0].clientX;
    const diff = currentX - startX;

    if (diff > SWIPE_THRESHOLD) {
      onReply?.({
        messageID,
        message: parsedMessage?.message || message,
        isDoctor,
      });
    }
    setTranslateX(0);
    setIsDragging(false);
  };

  const handleTouchCancel = () => {
    setTranslateX(0);
    setIsDragging(false);
  };

  const getTruncatedMessage = (msg: string | any): string => {
    if (!msg) return '';
  
    const msgStr = typeof msg === 'string' ? msg : JSON.stringify(msg);
    const maxChars = 100;
    const maxLines = 2;
    
    const lines = msgStr.split('\n');
    
    if (lines.length > maxLines) {
      return lines.slice(0, maxLines).join('\n') + '...';
    }
    
    if (msg.length > maxChars) {
      return msg.substring(0, maxChars) + '...';
    }
    
    return msg;
  };

  const getSenderMessageText = (): string => {
    if (!replyTo) return '';

    try {
      if (replyTo.senderUserID === userDokter) {
        return doctorName;
      } else {
        return patientName || 'Pasien';
      }
    } catch (e) {
      return 'Pengguna';
    }
  };

  const getReplyMessageText = (): string => {
    if (!replyTo) return '';
    
    try {
      // ✅ PERBAIKAN: Format Flutter - value_reply adalah pesan yang di-reply
      const messageContent = replyTo.value_reply || replyTo.message || '';
      return getTruncatedMessage(messageContent);
    } catch (e) {
      return getTruncatedMessage(replyTo.message || '');
    }
  };

  const isImageByUrl = (url: string) => {
    const clean = url.split("?")[0].toLowerCase();
    return /\.(jpg|jpeg|png|gif|webp|bmp)$/.test(clean);
  };

  return (
    <MessageContainer 
      ref={ref || messageRef}
      isDoctor={isDoctor}
      translateX={translateX}
      isHighlighted={isHighlighted}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
      style={{ userSelect: 'none', touchAction: 'pan-y' }}
    >
      <MessageWrapper>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "4px" }}>
          {isDoctor && (
            <Avatar
              src={avatar}
              alt="Doctor"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
        </div>
        <div>
          <MessageBubble isDoctor={isDoctor}>
            {replyTo && (
              <ReplyQuote
                clickable={true}
                onClick={() => onReplyQuoteClick?.(replyTo.messageID)}
              >
                <QuoteSender>{getSenderMessageText()}</QuoteSender>
                <QuoteMessage>{getReplyMessageText()}</QuoteMessage>
              </ReplyQuote>
            )}

            {resolvedImageUrl && /\.pdf$/i.test(resolvedFileName || "") ? (
              <a 
                href={resolvedImageUrl}
                download={resolvedFileName || "document.pdf"}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ textDecoration: "none" }}
              >
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  cursor: "pointer",
                  padding: "10px 12px",
                  background: "#f0f0f0",
                  borderRadius: "10px",
                  minWidth: "180px",
                  maxWidth: "240px",
                }}>
                  <span style={{ fontSize: "28px", flexShrink: 0 }}>{getFileIcon(resolvedFileName)}</span>
                  <div style={{ overflow: "hidden" }}>
                    <div style={{
                      fontSize: "13px",
                      color: "#222",
                      fontWeight: 500,
                      wordBreak: "break-all",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical" as any,
                      overflow: "hidden",
                    }}>
                      {resolvedFileName || "Dokumen PDF"}
                    </div>
                    <div style={{ fontSize: "11px", color: "#888", marginTop: "2px" }}>
                      {resolvedFileSize ? formatFileSize(resolvedFileSize) : "0.00 MB"}
                    </div>
                  </div>
                </div>
              </a>
            ) : resolvedImageUrl && (
              resolvedMediaKind === "image" ||
              /\.(jpg|jpeg|png|gif|webp)$/i.test(resolvedFileName || "") ||
              isImageByUrl(resolvedImageUrl)
            ) ? (
              <img
                src={resolvedImageUrl}
                alt={resolvedFileName || "Image"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onMediaClick?.(resolvedImageUrl, resolvedFileName, "image");
                }}
                style={{ maxWidth: "200px", maxHeight: "200px", borderRadius: "8px", cursor: "pointer" }}
              />
            ) : resolvedImageUrl && resolvedMediaKind === "video" ? (
              <video
                src={resolvedImageUrl}
                controls
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: "220px", maxHeight: "220px", borderRadius: "8px", display: "block" }}
              />
            ) : resolvedImageUrl && resolvedMediaKind === "audio" ? (
              <audio
                src={resolvedImageUrl}
                controls
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: "220px", display: "block" }}
              />
            ) : resolvedImageUrl ? (
              <a href={resolvedImageUrl}
                download={resolvedFileName || true}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ textDecoration: "none" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    cursor: "pointer",
                    padding: "10px 12px",
                    background: "#f0f0f0",
                    borderRadius: "10px",
                    minWidth: "180px",
                    maxWidth: "240px",
                  }}
                >
                  <span style={{ fontSize: "28px", flexShrink: 0 }}>
                    {getFileIcon(resolvedFileName)}
                  </span>
                  <div style={{ overflow: "hidden" }}>
                    <div style={{
                      fontSize: "13px",
                      color: "#222",
                      fontWeight: 500,
                      wordBreak: "break-all",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical" as any,
                      overflow: "hidden",
                    }}>
                      {resolvedFileName || "Download file"}
                    </div>
                    <div style={{ fontSize: "11px", color: "#888", marginTop: "2px" }}>
                      Tap untuk download
                    </div>
                  </div>
                </div>
              </a>
            ) : isObjectMessage ? (
              parsedMessage?.type === "new-summary" ? (
                parsedMessage["type-summary"] === "soap" ? (
                  <Summary summary={parsedMessage.summary} doctorName={doctorName} doctorSpecialty={doctorSpecialty} />
                ) : parsedMessage["type-summary"] === "prescription" ? (
                  <Prescription summary={parsedMessage.summary} />
                ) : parsedMessage["type-summary"] === "reference" ? (
                  <Reference summary={parsedMessage.summary} />
                ) : parsedMessage["type-summary"] === "certificate" ? (
                  <Certificate summary={parsedMessage.summary} />
                ) : null
              ) : (
                <div>{parsedMessage?.message}</div>
              )
            ) : (
              <span>{typeof parsedMessage === "object" ? parsedMessage?.message : parsedMessage}</span>
            )}

            <Timestamp>{timestamp}</Timestamp>
          </MessageBubble>
        </div>
      </MessageWrapper>
    </MessageContainer>
  );
});
export default ChatMessage;
